import { Injectable } from '@angular/core';
import { CampaignType } from '@libs/apis';
import { TaxonomyHelper } from '../helper/taxonomy-helper';
import { TaxonomyFeature } from '../models/TaxonomyFeature';
import { TaxonomyAction } from '../models/TaxonomyAction';

@Injectable({
  providedIn: 'root'
})
export class CampaignsConfirmationPopupService {
  public show = false;
  public campaignCount = 0;
  public title = '';
  public confirmationMessage = '';
  public campaignType!: CampaignType | TaxonomyFeature.AI | null;
  public allCampaignsSelected = false;
  public action!: TaxonomyAction;

  public showDialog(
    campaignCount: number,
    campaignType: CampaignType | TaxonomyFeature.AI | null,
    allCampaignsSelected = false,
    action: TaxonomyAction
  ): void {
    this.show = true;
    this.campaignCount = campaignCount;
    this.campaignType = campaignType;
    this.allCampaignsSelected = allCampaignsSelected;
    this.action = action;
    this.title = this.getTitle();
    this.confirmationMessage = this.getConfirmationMessage();
  }

  public hideDialog(): void {
    this.show = false;
    this.campaignCount = 0;
    this.campaignType = null;
    this.confirmationMessage = '';
    this.title = '';
    this.allCampaignsSelected = false;
    this.action = TaxonomyAction.APPROVE;
  }

  public get confirmationDialog() {
    return {
      show: this.show,
      campaignCount: this.campaignCount,
      campaignType: this.campaignType,
      confirmationMessage: this.confirmationMessage,
      title: this.title
    };
  }

  private getTitle(): string {
    const campaignText = this.allCampaignsSelected
      ? 'all campaigns'
      : `${this.campaignCount.toLocaleString()} ${TaxonomyHelper.getCampaignString(
          this.campaignCount
        )}`;

    switch (this.campaignType) {
      case CampaignType.MAPPED_CAMPAIGN:
        return this.action === TaxonomyAction.EDIT
          ? `Edit campaign mapping`
          : `Unamp campaigns`;
      case CampaignType.UNMAPPED_CAMPAIGN:
        return `Approve Mapping for ${campaignText}`;
      case CampaignType.UNDER_PROCESSING_CAMPAIGN:
        return this.action === TaxonomyAction.EDIT
          ? 'Modify campaign mapping'
          : 'Unmap campaigns';
      case TaxonomyFeature.AI:
        return `Approve campaign mapping`;
      default:
        return `Approve campaign mapping`;
    }
  }

  private getConfirmationMessage(): string {
    const campaignText = this.allCampaignsSelected
      ? 'all campaigns in this group'
      : `${this.campaignCount.toLocaleString()} ${TaxonomyHelper.getCampaignString(
          this.campaignCount
        )}`;

    switch (this.campaignType) {
      case CampaignType.MAPPED_CAMPAIGN:
        if (this.action === TaxonomyAction.EDIT) {
          return `You are about to edit the existing mapping of the selected campaigns. Changes will take effect in the next 2-3 hours.`;
        } else {
          return `You are about to unmap ${campaignText}. If you proceed, changes will take effect in the next 2-3 hours.`;
        }
      case CampaignType.UNMAPPED_CAMPAIGN:
        if (this.allCampaignsSelected) {
          return `You are about to approve mapping for ${campaignText}. Changes will take effect in the next 2-3 hours.`;
        } else {
          return `You are about to approve mapping for ${campaignText} in this group. Changes will take effect in the next 2-3 hours.`;
        }
      case CampaignType.UNDER_PROCESSING_CAMPAIGN:
        return `Mapping is already in progress for the selected campaigns. If you proceed, changes will take effect in the next 2-3 hours.`;
      case TaxonomyFeature.AI:
        if (this.action === TaxonomyAction.APPROVE_ALL) {
          return `You are about to approve mapping for all campaigns under 'AI Recommendations'. Changes will take effect in the next 2-3 hours.`;
        } else if (this.allCampaignsSelected) {
          return `You are about to approve mapping for ${campaignText}. Changes will take effect in the next 2-3 hours.`;
        } else {
          return `You are about to approve mapping for ${campaignText} in this group. Changes will take effect in the next 2-3 hours.`;
        }
      default:
        return ``;
    }
  }
}
